import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Images, Loader2 } from "lucide-react";
import { createSignedPatientFileUrls } from "@/lib/patientFilesApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Renders an ortho visit's photos (those with a real storage_path) as thumbnails.
// Click a thumbnail to open a lightbox with a download button. Photos that only
// have a filename (legacy, pre-upload) are ignored — there's no file to show.
export default function OrthoVisitPhotos({ photos }) {
  const stored = useMemo(
    () => (Array.isArray(photos) ? photos : []).filter((p) => p?.storage_path),
    [photos],
  );
  const pathsKey = stored.map((p) => p.storage_path).join("|");

  const [urls, setUrls] = useState({});
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!pathsKey) { setUrls({}); return undefined; }
    createSignedPatientFileUrls(pathsKey.split("|"))
      .then((map) => { if (!cancelled) setUrls(map || {}); })
      .catch(() => { if (!cancelled) setUrls({}); });
    return () => { cancelled = true; };
  }, [pathsKey]);

  async function downloadActive() {
    if (!active?.url) return;
    try {
      const res = await fetch(active.url);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = active.name || "ortho-photo.jpg";
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(active.url, "_blank", "noopener");
    }
  }

  if (!stored.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {stored.map((p, i) => {
        const url = urls[p.storage_path];
        return (
          <button
            key={p.storage_path || i}
            type="button"
            onClick={() => url && setActive({ url, name: p.name })}
            className="h-12 w-12 overflow-hidden rounded-md border border-slate-200 bg-slate-100"
            title={p.name || "Photo"}
            aria-label={`View photo ${p.name || i + 1}`}
          >
            {url ? <img src={url} alt={p.name || "Ortho photo"} className="h-full w-full object-cover" /> : null}
          </button>
        );
      })}

      {active ? (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/70 p-4"
          onClick={() => setActive(null)}
        >
          <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img src={active.url} alt={active.name || "Ortho photo"} className="max-h-[80vh] max-w-[90vw] rounded-lg object-contain" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={downloadActive}
                className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100"
              >
                Download
              </button>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded-lg border border-white/60 bg-transparent px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function OrthoVisitPhotoPreview({ photos, visitLabel = "Ortho visit" }) {
  const stored = useMemo(
    () => (Array.isArray(photos) ? photos : []).filter((photo) => photo?.storage_path),
    [photos],
  );
  const pathsKey = stored.map((photo) => photo.storage_path).join("|");
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!open || !pathsKey) return undefined;

    setLoading(true);
    setError("");
    createSignedPatientFileUrls(pathsKey.split("|"))
      .then((map) => {
        if (cancelled) return;
        setUrls(map || {});
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setUrls({});
        setLoading(false);
        setError("Could not load these visit photos. Close the preview and try again.");
      });

    return () => { cancelled = true; };
  }, [open, pathsKey]);

  if (!stored.length) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold text-teal-700 hover:bg-teal-50 hover:text-teal-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          data-testid="doctor-ortho-see-photos"
        >
          <Images className="h-3.5 w-3.5" />
          See photos
        </button>
      </DialogTrigger>

      <DialogContent
        className="max-h-[90vh] max-w-5xl gap-0 overflow-hidden border-slate-200 bg-white p-0"
        data-testid="doctor-ortho-photo-preview-dialog"
      >
        <DialogHeader className="border-b border-slate-100 px-5 py-4 pr-12">
          <DialogTitle className="text-left text-lg text-slate-950">{visitLabel} photos</DialogTitle>
          <DialogDescription className="text-left text-sm text-slate-500">
            {stored.length} photo{stored.length === 1 ? "" : "s"} attached to this visit.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(90vh-88px)] overflow-y-auto bg-slate-50 p-4 sm:p-5">
          {loading ? (
            <div className="flex min-h-64 items-center justify-center gap-2 text-sm font-medium text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading photos...
            </div>
          ) : error ? (
            <div className="flex min-h-64 items-center justify-center px-6 text-center text-sm font-medium text-rose-700" role="alert">
              {error}
            </div>
          ) : (
            <div className={stored.length === 1 ? "mx-auto max-w-3xl" : "grid gap-4 sm:grid-cols-2"}>
              {stored.map((photo, index) => {
                const url = urls[photo.storage_path];
                const name = photo.name || `Ortho photo ${index + 1}`;
                return (
                  <figure key={photo.storage_path} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex min-h-56 items-center justify-center bg-slate-100 p-2 sm:min-h-72">
                      {url ? (
                        <img
                          src={url}
                          alt={name}
                          className="max-h-[62vh] max-w-full rounded-lg object-contain"
                        />
                      ) : (
                        <span className="text-sm text-slate-400">Preview unavailable</span>
                      )}
                    </div>
                    <figcaption className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="min-w-0 truncate text-xs font-medium text-slate-600">{name}</span>
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-50 hover:text-teal-900"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Open
                        </a>
                      ) : null}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
